import { useState, useCallback, useEffect } from 'react'
import { toast } from '@sales-center/components'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import {
  getAppointment,
  completeAppointment,
  type AppointmentCompleteInput,
} from '@user/store/scheduling'
import type { CompleteAppointmentProps } from '../typing'

export const useCompleteAppointment = (params: CompleteAppointmentProps) => {
  const { onClose, onCloseMain, appointmentId, entityId, entityType } = params

  const dispatch = useAppDispatch()

  const [status, setStatus] = useState<'idle' | 'pending' | 'success' | 'fail'>(
    'idle',
  )

  const [input, setInput] = useState<AppointmentCompleteInput>()

  const execute = useCallback(
    (appointmentInput: AppointmentCompleteInput) => {
      if (status != 'pending') {
        setInput(appointmentInput)
      }
    },
    [status],
  )

  useEffect(() => {
    let mounted = true

    const execute = async () => {
      if (input) {
        try {
          setStatus('pending')

          const response = await dispatch(completeAppointment(input))

          if (completeAppointment.fulfilled.match(response)) {
            if (response.payload?.success) {
              toast.success('Appointment has been completed.')
              onClose()
              onCloseMain()
              if (appointmentId) {
                dispatch(
                  getAppointment({
                    entityId,
                    entityType,
                    appointmentId,
                  }),
                )
              }
            } else {
              setStatus('fail')
              toast.error(
                response.payload?.message ?? 'Failed to complete appointment.',
              )
            }
          } else {
            setStatus('fail')
            toast.error(`Unable to complete appointment.`)
          }
        } catch (error) {
          if (mounted) {
            setStatus('fail')
            toast.error(error.Message)
          }
        }
      }
    }
    execute()

    return () => {
      mounted = false
    }
  }, [
    dispatch,
    input,
    onClose,
    onCloseMain,
    appointmentId,
    entityId,
    entityType,
  ])

  return { status, execute }
}
